import { useCallback, useEffect, useState } from 'react';
import { FileUp, LayoutTemplate, Plus, Search, Sparkles, Trash2, X } from 'lucide-react';
import { Button, Input } from '@/design-system';
import * as dashboardApi from '../api/dashboard-api';
import { useFunnelListQuery } from '../hooks/useFunnelListQuery';
import { emptyStateMessage } from '../utils/empty-state-message';
import { FunnelCard } from './FunnelCard';
import { TextInputDialog } from './TextInputDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { MoveToFolderDialog } from './MoveToFolderDialog';
import { FolderNav } from './FolderNav';
import { TagFilter } from './TagFilter';
import { navigate } from '@/app/router';
import type { FunnelListResponse, FunnelSummary, FunnelStatus } from '@/contracts/funnel';
import { ImportDialog } from '@/features/import/ImportDialog';
import { RecentActivity } from '@/features/activity/RecentActivity';
import { CreateWithAIDialog } from './CreateWithAIDialog';

const STATUS_TABS: { value: FunnelStatus | 'favorites'; label: string }[] = [
  { value: 'active', label: 'Ativos' },
  { value: 'favorites', label: 'Favoritos' },
  { value: 'archived', label: 'Arquivados' },
];

/**
 * SPEC-003 §37/§38 — replaces the SPEC-002 authenticated placeholder.
 * Orchestrates search/filters/folders/tags/grid/pagination/dialogs. Kept
 * as one page-level component per plan §39 ("no global dashboard store
 * without need") — state stays local + URL-reflected.
 */
export function DashboardPage() {
  const [query, setQuery] = useFunnelListQuery();
  const [response, setResponse] = useState<FunnelListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchDraft, setSearchDraft] = useState(query.q);
  const [createOpen, setCreateOpen] = useState(false);
  const [createAIOpen, setCreateAIOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [renaming, setRenaming] = useState<FunnelSummary | null>(null);
  const [deleting, setDeleting] = useState<FunnelSummary | null>(null);
  const [movingFolder, setMovingFolder] = useState<FunnelSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    params.set('status', query.status);
    if (query.favorite) params.set('favorite', 'true');
    if (query.folderId) params.set('folderId', query.folderId);
    if (query.tagId) params.set('tagId', query.tagId);
    params.set('sort', query.sort);
    params.set('page', String(query.page));

    try {
      const res = await dashboardApi.listFunnels(params);
      setResponse(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchDraft(query.q);
  }, [query.q]);

  function handleStatusTab(tab: FunnelStatus | 'favorites') {
    if (tab === 'favorites') {
      setQuery({ status: 'active', favorite: true });
    } else {
      setQuery({ status: tab, favorite: false });
    }
  }

  const activeTab: FunnelStatus | 'favorites' = query.favorite ? 'favorites' : query.status;

  async function handleToggleFavorite(funnel: FunnelSummary) {
    if (!response) return;
    const next = !funnel.isFavorite;
    setResponse({
      ...response,
      items: response.items.map((item) =>
        item.id === funnel.id ? { ...item, isFavorite: next } : item,
      ),
    });
    try {
      await dashboardApi.updateFunnel(funnel.id, { isFavorite: next });
    } catch {
      // rollback (AC-032)
      setResponse((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === funnel.id ? { ...item, isFavorite: funnel.isFavorite } : item,
              ),
            }
          : current,
      );
    }
  }

  async function handleDuplicate(funnel: FunnelSummary) {
    await dashboardApi.duplicateFunnel(funnel.id);
    await load();
  }

  async function handleArchiveToggle(funnel: FunnelSummary) {
    if (funnel.status === 'archived') {
      await dashboardApi.unarchiveFunnel(funnel.id);
    } else {
      await dashboardApi.archiveFunnel(funnel.id);
    }
    await load();
  }

  const message = response ? emptyStateMessage(query, response.total) : null;

  return (
    <main className="mx-auto flex max-w-6xl gap-6 px-6 py-8">
      <aside className="w-48 shrink-0">
        <FolderNav
          selectedFolderId={query.folderId}
          onSelect={(folderId) => setQuery({ folderId })}
        />
      </aside>

      <div className="flex-1">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-text-primary">Funis</h1>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/app/trash')}>
              <Trash2 size={16} /> Lixeira
            </Button>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <FileUp size={16} /> Importar funil
            </Button>
            <Button variant="secondary" onClick={() => navigate('/app/templates')}>
              <LayoutTemplate size={16} /> Usar modelo
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus size={16} /> Funil em branco
            </Button>
            <Button variant="secondary" onClick={() => setCreateAIOpen(true)}>
              <Sparkles size={16} /> Novo funil com IA
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setQuery({ q: searchDraft });
            }}
            className="relative"
          >
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <Input
              aria-label="Buscar funis"
              placeholder="Buscar funis…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="w-56 pl-8"
            />
            {searchDraft ? (
              <button
                type="button"
                aria-label="Limpar busca"
                onClick={() => {
                  setSearchDraft('');
                  setQuery({ q: '' });
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
              >
                <X size={14} />
              </button>
            ) : null}
          </form>

          <div
            role="tablist"
            aria-label="Status dos funis"
            className="flex gap-1 rounded-md border border-border p-0.5"
          >
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.value}
                onClick={() => handleStatusTab(tab.value)}
                className={`rounded px-3 py-1 text-sm ${
                  activeTab === tab.value
                    ? 'bg-accent text-[#070807]'
                    : 'text-text-secondary hover:bg-surface-2'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <TagFilter selectedTagId={query.tagId} onSelect={(tagId) => setQuery({ tagId })} />
        </div>

        {loading ? (
          <div role="status" className="py-16 text-center text-sm text-text-muted">
            Carregando funis…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-text-secondary">Não foi possível carregar seus funis.</p>
            <Button variant="secondary" onClick={() => void load()}>
              Tentar novamente
            </Button>
          </div>
        ) : message ? (
          <div className="py-16 text-center text-sm text-text-muted">{message}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {response!.items.map((funnel) => (
                <FunnelCard
                  key={funnel.id}
                  funnel={funnel}
                  onToggleFavorite={handleToggleFavorite}
                  onRename={setRenaming}
                  onDuplicate={handleDuplicate}
                  onArchiveToggle={handleArchiveToggle}
                  onDelete={setDeleting}
                  onMoveToFolder={setMovingFolder}
                />
              ))}
            </div>

            {response!.total > response!.limit ? (
              <div className="mt-6 flex items-center justify-center gap-3">
                <Button
                  variant="secondary"
                  disabled={query.page <= 1}
                  onClick={() => setQuery({ page: query.page - 1 })}
                >
                  Anterior
                </Button>
                <span className="text-sm text-text-muted">Página {query.page}</span>
                <Button
                  variant="secondary"
                  disabled={!response!.hasMore}
                  onClick={() => setQuery({ page: query.page + 1 })}
                >
                  Próxima
                </Button>
              </div>
            ) : null}
          </>
        )}
        <RecentActivity />
      </div>

      <TextInputDialog
        open={createOpen}
        title="Novo funil"
        label="Nome do funil"
        initialValue="Novo funil"
        submitLabel="Criar"
        onClose={() => setCreateOpen(false)}
        onSubmit={async (name) => {
          const { funnel } = await dashboardApi.createFunnel(name);
          navigate(`/app/funnels/${funnel.id}`);
        }}
      />

      <CreateWithAIDialog open={createAIOpen} onClose={() => setCreateAIOpen(false)} />

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />

      <TextInputDialog
        open={renaming !== null}
        title="Renomear funil"
        label="Nome do funil"
        initialValue={renaming?.name}
        onClose={() => setRenaming(null)}
        onSubmit={async (name) => {
          if (!renaming) return;
          await dashboardApi.updateFunnel(renaming.id, { name });
          await load();
        }}
      />

      <DeleteConfirmDialog
        open={deleting !== null}
        funnelName={deleting?.name ?? ''}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await dashboardApi.deleteFunnel(deleting.id);
          await load();
        }}
      />

      <MoveToFolderDialog
        funnel={movingFolder}
        onClose={() => setMovingFolder(null)}
        onMoved={load}
      />
    </main>
  );
}
